import { cloneDeep } from 'lodash-es'
import { defineStore } from 'pinia'
import { computed, reactive, toRefs } from 'vue'
import { useRouter } from 'vue-router'

import { isGroupedPetSearchEnabled } from '@/lib/opsToggle'
import { defaultValues } from '@/store/fnol/defaults'
import { FnolStep, FnolState } from '@/store/fnol/types'

export const routes = {
  [FnolStep.FIND_POLICY]: '/hub/claims/new',
  [FnolStep.SELECT_PET]: '/hub/claims/new/select-pet',
  [FnolStep.CLAIM_TYPE]: '/hub/claims/new/claim-type',
  [FnolStep.EXISTING_CLAIMS]: '/hub/claims/new/existing-claims',
  [FnolStep.CLAIM_DETAILS]: '/hub/claims/new/claim-details',
  [FnolStep.REVIEW]: '/hub/claims/new/review',
  [FnolStep.CONFIRMATION]: '/hub/claims/new/confirmation',
} as const

export const titles = {
  [FnolStep.FIND_POLICY]: 'Find policy',
  [FnolStep.SELECT_PET]: 'Select pet',
  [FnolStep.CLAIM_TYPE]: 'Claim type',
  [FnolStep.EXISTING_CLAIMS]: 'Existing claims',
  [FnolStep.CLAIM_DETAILS]: 'Claim details',
  [FnolStep.REVIEW]: 'Review',
  [FnolStep.CONFIRMATION]: 'Confirmation',
} as const

export const stepTitles = {
  [FnolStep.FIND_POLICY]: 'New claim',
  [FnolStep.SELECT_PET]: 'Pet',
  [FnolStep.CLAIM_TYPE]: 'Type',
  [FnolStep.EXISTING_CLAIMS]: 'Existing claims',
  [FnolStep.CLAIM_DETAILS]: 'Details',
  [FnolStep.REVIEW]: 'Review',
  [FnolStep.CONFIRMATION]: 'Done',
} as const

export const useFnolStore = defineStore(
  'fnol',
  () => {
    const router = useRouter()

    // State
    const state = reactive<FnolState>({
      steps: [
        FnolStep.FIND_POLICY,
        FnolStep.SELECT_PET,
        FnolStep.CLAIM_TYPE,
        FnolStep.EXISTING_CLAIMS,
        FnolStep.CLAIM_DETAILS,
        FnolStep.REVIEW,
        FnolStep.CONFIRMATION,
      ],
      currentStep: FnolStep.FIND_POLICY,
      treatmentStartDate: '',
      treatmentEndDate: '',
      policies: [],
      activePolicies: [],
      findPolicy: cloneDeep(defaultValues.findPolicy),
      selectPet: cloneDeep(defaultValues.selectPet),
      claimType: cloneDeep(defaultValues.claimType),
      existingClaims: cloneDeep(defaultValues.existingClaims),
      claimDetails: cloneDeep(defaultValues.claimDetails),
      documents: cloneDeep(defaultValues.documents),
      review: cloneDeep(defaultValues.review),
    })

    // Getters
    const currentStepIndex = computed(() => {
      return state.steps.findIndex((step) => step === state.currentStep)
    })

    const showPolicyInfo = computed(() => {
      return !!(
        state.selectPet.pet &&
        state.selectPet.policy &&
        state.treatmentStartDate &&
        state.treatmentEndDate &&
        ![
          FnolStep.FIND_POLICY,
          FnolStep.SELECT_PET,
          FnolStep.CONFIRMATION,
        ].includes(state.currentStep)
      )
    })

    const canNavigateToStep = function (this: any, step: FnolStep) {
      const steps = state.steps
      const targetStepIndex = steps.findIndex((s) => s === step)

      if (targetStepIndex === -1) return false

      // Can navigate to next step if current step is completed
      const prevStep = steps[targetStepIndex - 1]
      if (!prevStep) return true

      return this.isStepCompleted(prevStep)
    }

    // Navigate to next step
    const goToNextStep = function (this: any, replace: boolean = false) {
      const steps = state.steps
      const currentIndex = currentStepIndex.value

      if (currentIndex < steps.length - 1) {
        const nextStep = steps[currentIndex + 1]
        this.navigateToStep(nextStep, replace)
      }
    }

    // Navigate to previous step
    const goToPreviousStep = function (this: any) {
      const steps = state.steps
      const currentIndex = currentStepIndex.value

      if (currentIndex > 0) {
        const prevStep = steps[currentIndex - 1]
        this.navigateToStep(prevStep)
      }
    }

    // Navigate to a specific step
    const navigateToStep = function (
      this: any,
      step: FnolStep,
      replace: boolean = false
    ) {
      if (this.canNavigateToStep(step)) {
        state.currentStep = step
        if (replace) {
          router.replace(routes[step])
        } else {
          router.push(routes[step])
        }
        resetAfterCurrentStep()
      }
    }

    // Helper to check if a step is completed
    // TODO - Implement this for each step by
    // working out what the store need sot look like for
    // this to be valid
    const isStepCompleted = (step: FnolStep) => {
      switch (step) {
        case FnolStep.FIND_POLICY:
          return true
        case FnolStep.SELECT_PET:
          return true
        case FnolStep.CLAIM_TYPE:
          return true
        case FnolStep.EXISTING_CLAIMS:
          return true
        case FnolStep.CLAIM_DETAILS:
          return true
        case FnolStep.REVIEW:
          return true
        case FnolStep.CONFIRMATION:
          return true
        default:
          return false
      }
    }

    // Reset the entire form
    const reset = () => {
      state.currentStep = FnolStep.FIND_POLICY
      state.treatmentStartDate = ''
      state.treatmentEndDate = ''
      state.policies = []
      state.activePolicies = []
      state.findPolicy = cloneDeep(defaultValues.findPolicy)
      state.selectPet = cloneDeep(defaultValues.selectPet)
      state.claimType = cloneDeep(defaultValues.claimType)
      state.existingClaims = cloneDeep(defaultValues.existingClaims)
      state.claimDetails = cloneDeep(defaultValues.claimDetails)
      state.documents = cloneDeep(defaultValues.documents)
      // We don't reset review here as it might be needed for the confirmation step
      // but resetAfterCurrentStep will be called if a user lands anywhere else
      // in the form flow
    }

    const resetAfterCurrentStep = () => {
      // Get the current step index
      const currentIndex = currentStepIndex.value

      // Reset all steps after the current step
      state.steps.slice(currentIndex + 1).forEach((step) => {
        switch (step) {
          case FnolStep.FIND_POLICY:
            state.findPolicy = cloneDeep(defaultValues.findPolicy)
            state.policies = cloneDeep(defaultValues.policies)
            if (!isGroupedPetSearchEnabled()) {
              state.treatmentStartDate = cloneDeep(
                defaultValues.treatmentStartDate
              )
              state.treatmentEndDate = cloneDeep(defaultValues.treatmentEndDate)
              state.activePolicies = cloneDeep(defaultValues.activePolicies)
            }
            break
          case FnolStep.SELECT_PET:
            state.selectPet = cloneDeep(defaultValues.selectPet)
            break
          case FnolStep.CLAIM_TYPE:
            state.claimType = cloneDeep(defaultValues.claimType)
            if (isGroupedPetSearchEnabled()) {
              state.treatmentStartDate = cloneDeep(
                defaultValues.treatmentStartDate
              )
              state.treatmentEndDate = cloneDeep(defaultValues.treatmentEndDate)
              state.activePolicies = cloneDeep(defaultValues.activePolicies)
            }
            break
          case FnolStep.EXISTING_CLAIMS:
            state.existingClaims = cloneDeep(defaultValues.existingClaims)
            break
          case FnolStep.CLAIM_DETAILS:
            state.claimDetails = cloneDeep(defaultValues.claimDetails)
            break
          case FnolStep.REVIEW:
            state.review = cloneDeep(defaultValues.review)
            break
        }
      })
    }

    // Redirect to the current step's route
    const redirectToCurrentStep = () => {
      const currentRoute = routes[state.currentStep]
      const currentPath = router.currentRoute.value.path

      // Get step for current path
      const currentPathStep = Object.entries(routes).find(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ([_, route]) => route === currentPath
      )?.[0] as FnolStep

      if (currentPathStep) {
        const currentPathIndex = state.steps.indexOf(currentPathStep)
        const allowedStepIndex = state.steps.indexOf(state.currentStep)

        // Only redirect if trying to access a step further than allowed
        if (currentPathIndex > allowedStepIndex) {
          router.push(currentRoute)
        } else {
          // Otherwise, update the current step to the current path
          state.currentStep = currentPathStep
        }
      }
      resetAfterCurrentStep()
    }

    return {
      ...toRefs(state),
      currentStepIndex,
      showPolicyInfo,
      canNavigateToStep,
      goToNextStep,
      goToPreviousStep,
      navigateToStep,
      isStepCompleted,
      reset,
      resetAfterCurrentStep,
      redirectToCurrentStep,
    }
  },
  {
    persist: true,
  }
)
